import { motion } from 'framer-motion';
import { Clock, Zap, TrendingDown, TrendingUp, Image, AlertTriangle, Award, Users, Sparkles } from 'lucide-react';

const nodes = [
  {
    id: 1,
    year: '2016-01',
    title: 'Ethereum Mainnet & Smart Contracts Birth',
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    description: 'Witness Vitalik\'s "World Computer", deploy your first smart contract. Six sub-phases: genesis block, first dApp, Solidity 0.1.0, The DAO preview, "Code is Law" debate, and community milestones. Staking, voting, and random events unlock the next node.',
    events: ['Solidity 0.1.0', 'The DAO Preview', 'Code is Law', 'First dApp', 'Genesis Block'],
    fullPreview: true,
  },
  {
    id: 2,
    year: '2016-07',
    title: 'Bitcoin Halving',
    fullPreview: false,
    icon: TrendingUp,
    color: 'from-orange-500 to-yellow-500',
    description: 'Block reward 25→12.5 BTC, deflationary economics validated',
    events: ['Block 630,000', 'Miner Anxiety', 'BTC vs ETH'],
  },
  {
    id: 3,
    year: '2016-05',
    title: 'The DAO Application',
    fullPreview: false,
    icon: Users,
    color: 'from-green-500 to-emerald-500',
    description: 'Largest crowdfund ever $150M, peak of decentralization idealism',
    events: ['28-day Crowdfund', 'No CEO Needed', 'Code Governance'],
  },
  {
    id: 4,
    year: '2016-07',
    title: 'The DAO Hack & Hard Fork',
    fullPreview: false,
    icon: AlertTriangle,
    color: 'from-red-500 to-pink-500',
    description: '3.6M ETH stolen, community split ETH/ETC, philosophical war',
    events: ['Hacker Attack', 'Fork Vote', 'Ethereum Split'],
  },
  {
    id: 5,
    year: '2017-09',
    title: 'ERC-20 & ICO Boom',
    fullPreview: false,
    icon: Sparkles,
    color: 'from-purple-500 to-pink-500',
    description: 'Everyone issues tokens, $150M in 3 hours, wealth myths & scams',
    events: ['Bancor ICO', 'Gas Wars', 'Whitepaper Scams'],
  },
  {
    id: 6,
    year: '2018-01',
    title: 'ICO Bubble & Bear Market',
    fullPreview: false,
    icon: TrendingDown,
    color: 'from-gray-500 to-slate-600',
    description: 'ETH $1400→$80 (-94%), 90% projects dead, faith tested',
    events: ['Crash', 'Rug Pulls', 'Survivors'],
  },
  {
    id: 7,
    year: '2020-09',
    title: 'Uniswap AMM & DeFi Summer',
    fullPreview: false,
    icon: TrendingUp,
    color: 'from-pink-500 to-purple-500',
    description: 'Liquidity mining 1000% APY, TVL from $1B to $100B',
    events: ['AMM Revolution', 'Sushiswap Vampire', 'YFI Fair Launch'],
  },
  {
    id: 8,
    year: '2021-08',
    title: 'ERC-721 NFT & Metaverse',
    fullPreview: false,
    icon: Image,
    color: 'from-cyan-500 to-blue-500',
    description: 'Ape JPEGs sell for millions, OpenSea $10B monthly volume',
    events: ['BAYC Mania', 'Virtual Land', 'JPEG Summer'],
  },
  {
    id: 9,
    year: '2022-11',
    title: 'Terra/Luna & FTX Collapse',
    fullPreview: false,
    icon: AlertTriangle,
    color: 'from-red-600 to-orange-600',
    description: 'UST depeg, 3AC bankruptcy, FTX $8B hole, trust shattered',
    events: ['Do Kwon Flees', 'SBF Arrested', 'Regulatory Crackdown'],
  },
  {
    id: 10,
    year: '2025-03',
    title: 'Layer2 & ETF Institutional Bull',
    fullPreview: false,
    icon: Award,
    color: 'from-yellow-500 to-amber-500',
    description: 'Base TVL $20B, BlackRock ETF approved, mainstream adoption',
    events: ['Institutions Enter', 'L2 Maturity', 'Grand Finale'],
  },
];

export default function TimelineSection() {
  return (
    <section className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
            Time-Travel Nodes
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Experience 10 major blockchain events from 2016-2025, make choices with known endings
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Center line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-500 via-pink-500 to-blue-500 transform -translate-x-1/2 hidden md:block" />

          {/* Node cards */}
          <div className="space-y-12">
            {nodes.map((node, index) => (
              <motion.div
                key={node.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col md:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Left/right content */}
                <div className="flex-1">
                  <motion.div
                    className="bg-slate-800/50 backdrop-blur-sm border border-purple-500/30 rounded-xl p-6 hover:border-purple-500/60 transition-all"
                    whileHover={{ scale: 1.02, y: -5 }}
                  >
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${node.color} flex items-center justify-center mb-4 mx-auto md:mx-0`}>
                      <node.icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Content */}
                    <div className="text-sm text-purple-300 mb-2">{node.year}</div>
                    <h3 className="text-2xl font-bold text-white mb-3">{node.title}</h3>
                    <p className="text-gray-400 mb-4">{node.description}</p>

                    {/* Event tags */}
                    <div className="flex flex-wrap gap-2">
                      {node.events.map((event) => (
                        <span
                          key={event}
                          className="px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs text-purple-300"
                        >
                          {event}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Center dot */}
                <div className="hidden md:block">
                  <motion.div
                    className={`w-6 h-6 rounded-full bg-gradient-to-br ${node.color} shadow-lg shadow-purple-500/50`}
                    whileHover={{ scale: 1.5 }}
                  />
                </div>

                {/* Spacer (other side) */}
                <div className="flex-1 hidden md:block" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Multiverse preview */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="inline-block bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/50 rounded-xl p-8 max-w-2xl">
            <Sparkles className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Multiverse Preview</h3>
            <p className="text-gray-400">
              After endgame, user-created parallel universes. Create your own timelines forever — 2008 Crisis, Cyberpunk 2077, or your story. Perpetual creation, no VC, only community.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
